import {makeAutoObservable, runInAction} from "mobx";
import {GroupedOption} from "../../../components/inputs/select-dropdown-grouped/select-dropdown-grouped";
import {IOption} from "../../../components/inputs/select-dropdown/select-dropdown";
import {RESOLUTION_CATEGORY} from "../../../models/ResolutionTypes";
import {AUTO_PROCESS_TRIGGER, AUTO_PROCESS_VALIDATED_QUALIFIER, ICreateRmaDefaultSettings, ICRMAFeeDto} from "../../../models/User";
import CreateRmaService, {CREATE_RMA_MODE, ICRmaReason, ICRmaResolution, IResolutionsCategoriesList, IReturnMethodsList, ICRmaPolicyRule, IStoreLocationsMap, createRmaOptions, REQUIRE_CRMA_FOLLOWUP_QUESTIONS} from "../../../services/api/create-rma-service";
import {rootContainer, TYPE} from "../../../services/container/root";
import {AUTO_PROCESS_EXECUTE, SINCE_UNIT} from "../../policies/constants";
import { CreateRmaMainStoreInstanceManager } from "./create-rma-main-store";
import { toBoolean, toNullableNumber, toNumber } from "../../../models/model-types-methods";
import { IPolicyFee } from "../../policies/policy-types";

type ICrmaModeOption = Omit<IOption, 'label'> & {
	labelTranslationKey: string;
}

class CreateRmaSettingsStore {
	private _isLoading:boolean = false;
	private _reasonsList: ICRmaReason[] = [];
	private _resolutionsCategoryList: IResolutionsCategoriesList[] = [];
	private _returnMethodsList: IReturnMethodsList[] = [];
	private _defaults: ICreateRmaDefaultSettings = {} as ICreateRmaDefaultSettings;
    private _locations: IStoreLocationsMap = [];
    private _currentLocation?: string;
	private _wasLoaded: boolean = false; /** an indication to prevent loading this store over and over */
	private _policyRules?: ICRmaPolicyRule[];
	private _cRmaMode: CREATE_RMA_MODE = CREATE_RMA_MODE.C_RMA_GOD_MODE; 
	private _allowedModes: CREATE_RMA_MODE[] = [];

	constructor(private _createRmaMainStoreInstanceManager?: CreateRmaMainStoreInstanceManager) {
		makeAutoObservable(this);
	}
	
	public get isLoading():boolean { return this._isLoading; }

	public get service():CreateRmaService {return rootContainer.get<CreateRmaService>(TYPE.CreateRmaService);}
    get merchantReturnReasonId() { return this._defaults.merchant_return_reason_id; }
    get merchantResolutionId() { return this._defaults.resolution_id; }
    get returnMethodId() { return this._defaults.return_method_id; }
    get adjustment(): number|null  { return this._defaults.adjustments; }
    get restockingFee(): number|null { return this._defaults.restocking_fee; }
    get locations(): IStoreLocationsMap { return this._locations; }
    get currentLocation(): string | undefined { return this._currentLocation; }
    
    public get isAutoRefund(): boolean { return this._defaults.is_auto_refund; }
    public get autoRefundTrigger(): AUTO_PROCESS_TRIGGER | null { return this._defaults.auto_refund_trigger; }
    
    public get autoRefundValidatedQualifier(): AUTO_PROCESS_VALIDATED_QUALIFIER | null { return this._defaults.auto_refund_validated_qualifier ?? null; }
    public get autoRefundExecute(): AUTO_PROCESS_EXECUTE | null { return this._defaults.auto_refund_execute;}
    public get autoRefundSinceValue(): number | null { return this._defaults.auto_refund_since_value ?? null; }
    public get autoRefundSinceUnit(): SINCE_UNIT | null { return this._defaults.auto_refund_since_unit ?? null; }
    public get autoMarkDone(): boolean { return this._defaults.auto_mark_done; }
	public get cRmaMode(): CREATE_RMA_MODE|undefined { return this._cRmaMode; }
	public get sendCustomerEmails(): boolean { return this._defaults.send_customer_emails ?? false; }
	public get wasLoaded(): boolean { return this._wasLoaded; }
	public get policyRules(): ICRmaPolicyRule[] | undefined { return this._policyRules; }
	/** temporary, remove after implementing fees calculations */
	public get isPolicyLedMode(): boolean { return this.cRmaMode === CREATE_RMA_MODE.C_RMA_POLICY_LED_MODE; }
	public get showFuqs(): boolean { return this._defaults.require_crma_followup_questions !== REQUIRE_CRMA_FOLLOWUP_QUESTIONS.DONT_SHOW; }
	public get requireFuqs(): REQUIRE_CRMA_FOLLOWUP_QUESTIONS { return this._defaults.require_crma_followup_questions; }
	public get createRmaFeeDefaults(): { return_fee: ICRMAFeeDto; label_fee: ICRMAFeeDto } {
		return { return_fee: this._defaults.return_fee, label_fee: this._defaults.label_fee };
	}

	public findPolicyRule(policyRuleId: number|undefined): ICRmaPolicyRule|undefined { 
		return this._policyRules?.find(rule => rule.id === policyRuleId); 
	}

	/** used by policy-led only */
	public findPolicyOrFail(id?: number): ICRmaPolicyRule {
        const policyRule = this.findPolicyRule(id);
        if (!policyRule) {
            throw new Error(`policy rule was not found by id ${id}, should never happen, cannot proceed from here`);
        }
        return policyRule;
    }

	public findPolicyFeesSettings(policyRuleId?: number): IPolicyFee[] {
		return this.findPolicyRule(policyRuleId)?.policy_rule_fees ?? [];
	}


	public findReturnMethod(returnMethodId: number|null|undefined): IReturnMethodsList|undefined { 
		return this._returnMethodsList.find(rm => rm.id === returnMethodId); 
	}

	public findResolution(resolutionId: number|null|undefined): ICRmaResolution|undefined {
		return this._resolutionsCategoryList.map(c => c.list).flat().find(res => res.id === resolutionId);
	}

	public setCRmaMode(v: CREATE_RMA_MODE) {
		this._cRmaMode = v;
		/** when mode is switched - we need to regenerate the request store, previous is no longer relevant */
		const rmaMainStore = this._createRmaMainStoreInstanceManager?.getInstance();
		rmaMainStore?.createRmaRequestStore();
	}

	public getCRmaModeOptions(): ICrmaModeOption[] {
		return createRmaOptions().filter(opt => !!this._allowedModes?.includes(opt.value));
	}

	public async load() {
		if (this._wasLoaded || this._isLoading) {
			return;
		}

		try {
			this._isLoading = true;
			const settings = await this.service.fetchSettings();

			runInAction(() => {
				this._isLoading = false;
				this._wasLoaded = true;

				this._reasonsList = settings.reasons_list;
				this._resolutionsCategoryList = settings.resolutions_list;
				this._returnMethodsList = settings.return_methods_list;
                this._defaults = ensureValidDefaultsModel(settings.defaults);
                this._locations = settings.store_locations;
                this._currentLocation = settings.current_location;
				this._policyRules =  ensureValidPolicies(settings.policy_rules);
				this._allowedModes = settings.allowed_modes;

				/** preselet mode when returned one mode only */
				if (this.getCRmaModeOptions().length === 1) {
					this._cRmaMode = this.getCRmaModeOptions()[0].value as CREATE_RMA_MODE;
				}
			})
		}
		catch (e) {
			console.error(e);
			runInAction(() => {
				this._isLoading = false;
			});
		}
	}

    /**
     * 
     * @param returnReasonId
     * @returns selected reason (IReasonsList) or undefined if no selected option found
     */
    public findReason(returnReasonId: number|undefined|null): ICRmaReason|undefined {
        return this._reasonsList.find(r => r.id === returnReasonId);
    }

    /**
     * 
     * @param returnReasonId
     * @returns selected IOption or undefined if no selected option found
     */
    public findReasonOption(returnReasonId: number|undefined|null): IOption|undefined {
        return this.getReasonsListOptions().find(o => o.value === returnReasonId);
    }
	
    /**
     * 
     * @param filterEnabled that defines what reasons we include in returned IOption list
     * - null - we include all reasons regardless enabled or not. DEFAULT
     * - true - we include only enabled reasons
     * - false - we include only disabled reasons
     * 
     * @returns IOption[]
     */
    public getReasonsListOptions(filterEnabled: null|boolean = null): IOption[] {
        const reasonsList = (filterEnabled === null) ? this._reasonsList : this._reasonsList.filter(r => Boolean(r.is_enabled) === filterEnabled);
        return reasonsList.map((item): IOption => {
            return {
                label: item.internal_name,
                value: item.id
            }
        });
    }
	
    public getResolutionsListOptions(): GroupedOption[] {
	    const textByCategoryMap = {
		    [RESOLUTION_CATEGORY.exchange_resolutions]: {
			    title: 'Exchange',
		    },
		    [RESOLUTION_CATEGORY.standard_resolutions]: {
			    title: 'Return Resolutions',
		    },
		    [RESOLUTION_CATEGORY.returngo_resolutions]: {
			    title: 'ReturnScore Instant Credit',
		    },
		    [RESOLUTION_CATEGORY.cancel_order_resolutions]: {
			    title: 'Order Modification',
		    },
		    [RESOLUTION_CATEGORY.other_resolutions]: { /** no-offers is not fetched for C-RMA, not available for C-RMA god-mode */
			    title: 'No Offers',
		    },
	    }
	    
	    return  this._resolutionsCategoryList
		    //for phase 1 filter out exchange resolutions
		    .filter((group)=> ![RESOLUTION_CATEGORY.exchange_resolutions].includes(group.category))
		    .map((group: IResolutionsCategoriesList) => ({
				category: textByCategoryMap[group.category].title,
				options: group.list.map(resolution => ({
					label: resolution.internal_name,
					value: resolution.id
				}))
			}));
    }

	public getReturnMethodsListOptions():IOption[] {
		return this._returnMethodsList.map((item):IOption =>{
            return {
                label: item.internal_name,
                value: item.id
            };
        });
	}
    
	public isLabelCostSupported(returnMethodId: number | null): boolean {
		return this._returnMethodsList.find( rm => rm.id === returnMethodId)?.is_label_cost_supported ?? false;
	}
}


export default CreateRmaSettingsStore;

function ensureValidDefaultsModel(model: ICreateRmaDefaultSettings): ICreateRmaDefaultSettings {
    return {
        ...model,
        restocking_fee: toNullableNumber(model.restocking_fee),
        adjustments: toNullableNumber(model.adjustments),
		is_auto_refund: toBoolean(model.is_auto_refund),
		auto_mark_done: toBoolean(model.auto_mark_done),
		send_customer_emails: toBoolean(model.send_customer_emails),
    }
}

function ensureValidPolicies(policies: ICRmaPolicyRule[]): ICRmaPolicyRule[] {
	return policies.map(model => {
		return {
			...model,
			policy_rule_fees: ensureValidFeesModel(model.policy_rule_fees),
		};
	});
}

function ensureValidFeesModel(fees: IPolicyFee[]): IPolicyFee[] {
	return fees.map(model => {
		return {
			...model,
			is_charged: toBoolean(model.is_charged),
			charge_tax: toBoolean(model.charge_tax),
			amount: toNumber(model.amount),
		};
	});
}